import { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import CustomSkeleton from 'src/components/skeleton/Skeleton';
import * as Yup from 'yup';
import FormikForm, { FormikStep } from 'src/components/ui-components/FormikForm';
import InputField from 'src/components/ui-components/InputField';
import SwitchField from 'src/components/ui-components/SwitchField';
import SelectField from 'src/components/ui-components/SelectField';
import { getIcons, insertUpdatePageGroup } from './ConnectionService';
import AlertMessage from 'src/components/ui-components/AlertMessage';

const defaultForm = {
  pageGroupName: '',
  componentTag: '',
  pageGroupAddress: '',
  sortOrder: '',
  iconMasterKey: null,
  allow: '',
  isSystemGroup: true,
  isFooterGroup: false,
  isEnabled: false,
  isVisible: false,
};

type MenusCreateEditProps = {
  selectedMenu?: string | null;
  menuDetails?: any | null;
  userGroupKey?: string | null;
  reloadTree?: any | null;
  newSortOrder?: number | null;
  setIsCheckboxDisabled?: any | null;
  resetFilters?: any | null;
  handleMenuSelect?: any | null;
  filters?: any | null;
  updateFilters?: any | null;
  checkIfMenuHasSubMenus?: any | null;
};

const MenusCreateEdit: FC<MenusCreateEditProps> = ({ selectedMenu, menuDetails, userGroupKey, reloadTree, newSortOrder, setIsCheckboxDisabled, resetFilters, handleMenuSelect, filters, updateFilters, checkIfMenuHasSubMenus }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(!!selectedMenu);
  const [iconData, setIconData] = useState([]);
  const [isCreated, setIsCreated] = useState<boolean>(false);
  const formRef = useRef(null);

  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data, isEdit: selectedMenu && true },
      });
    }
  };

  useEffect(() => {
    setIsFetching(true);
    if (selectedMenu && menuDetails) {
      resetFormData({
        pageGroupName: menuDetails.pageGroupName || '',
        componentTag: menuDetails.componentTag || '',
        pageGroupAddress: menuDetails.pageGroupAddress || '',
        sortOrder: menuDetails.sortOrder || '',
        iconMasterKey: menuDetails.iconMasterKey || '',
        allow: menuDetails.allow || '',
        isSystemGroup: menuDetails.isSystemGroup || true,
        isFooterGroup: menuDetails.isFooterGroup || false,
        isEnabled: menuDetails.isEnabled || false,
        isVisible: menuDetails.isVisible || false,
      });
    } else {
      resetFormData({
        ...defaultForm,
        sortOrder: newSortOrder || '',
      });
    }
    setIsFetching(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenu, menuDetails, newSortOrder]);

  const handleSave = async (values) => {
    setIsLoading(true);

    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => {
        if (typeof value === 'string') {
          return [key, value.trim()];
        } else {
          return [key, value];
        }
      })
    );

    const requestObject = {
      ...trimmedValues,
      pageGroupKey: selectedMenu || null,
      iconMasterKey: values.iconMasterKey || null,
      userGroupKey: userGroupKey,
      pagesFiltersMappingsRequest: filters
        .filter(filter => filter.selected)
        .map(filter => ({ pageFilterKey: filter.id }))
    };

    try {
      await insertUpdatePageGroup(requestObject);
      const msg = values.isEdit
        ? "Menu updated successfully"
        : "Menu created successfully";
      AlertMessage({ type: 'success', msg });
      const sortedFilters = [...filters].sort((a, b) => b.selected - a.selected);
      setIsCheckboxDisabled(true);
      handleMenuSelect(selectedMenu);
      updateFilters(sortedFilters);
      setIsDisabled(true);
      setIsCreated(true);
      reloadTree(userGroupKey);
    } catch (err) {
      Object.entries(err.errors).forEach(([, errMsg]: any) => {
        AlertMessage({ type: 'error', msg: errMsg.errorMessage });
      });
      setIsCheckboxDisabled(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsDisabled(false);
    setIsCreated(true);

    const hasSubMenus = checkIfMenuHasSubMenus(selectedMenu);
    setIsCheckboxDisabled(hasSubMenus);
  };

  const handleCancel = () => {
    setIsCheckboxDisabled(true);
    resetFilters();
    if (menuDetails) {
      resetFormData({
        pageGroupName: menuDetails.pageGroupName || '',
        componentTag: menuDetails.componentTag || '',
        pageGroupAddress: menuDetails.pageGroupAddress || '',
        sortOrder: menuDetails.sortOrder || '',
        iconMasterKey: menuDetails.iconMasterKey || '',
        allow: menuDetails.allow || '',
        isSystemGroup: menuDetails.isSystemGroup || true,
        isFooterGroup: menuDetails.isFooterGroup || false,
        isEnabled: menuDetails.isEnabled || false,
        isVisible: menuDetails.isVisible || false,
      });
      setIsDisabled(true);
    } else {
      resetFormData({
        ...defaultForm,
        sortOrder: newSortOrder || '',
      });
      setIsDisabled(true);
    };
  };

  const getIconData = async () => {
    const res = await getIcons();
    setIconData(res);
  };

  useEffect(() => {
    getIconData();
  }, []);

  useEffect(() => {
    setIsDisabled(!!selectedMenu);
  }, [selectedMenu]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5">Menu Details</Typography>
        <Grid style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {!selectedMenu && !isCreated && (
            <Button
              variant="contained"
              color="primary"
              style={{ marginLeft: '10px' }}
              sx={{
                background:
                  "linear-gradient(to bottom, #072f40, #072f40)",
              }}
              onClick={() => formRef.current.submitForm()}
            >
              Save
              {isLoading && (
                <CircularProgress
                  color="inherit"
                  thickness={5}
                  size={20}
                  sx={{ ml: 1 }}
                />
              )}
            </Button>
          )}
          {(selectedMenu || isCreated) && (
            <>
              {isDisabled ? (
                <Button variant="contained" onClick={handleEdit} sx={{
                  background:
                    "linear-gradient(to bottom, #072f40, #072f40)",
                }}>
                  Edit
                </Button>
              ) : (
                <>
                  <Button sx={{ background: 'linear-gradient(to bottom, #939ca3, #939ca3)', minWidth: 100 }} variant="contained" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      background:
                        "linear-gradient(to bottom, #072f40, #072f40)",
                    }}
                    style={{ marginLeft: '10px' }}
                    onClick={() => formRef.current.submitForm()}
                  >
                    Save
                    {isLoading && (
                      <CircularProgress
                        color="inherit"
                        thickness={5}
                        size={20}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} md={12} lg={12} sm={12} xl={12}>
        {isFetching && <CustomSkeleton skeletonType="FormEdit" />}
        <Box sx={{ display: isFetching ? 'none' : '' }}>
          <FormikForm
            innerRef={formRef}
            initialValues={defaultForm}
            onSubmit={handleSave}
          >
            <FormikStep
              validationSchema={Yup.object({
                pageGroupName: Yup.string()
                  .required('Menu Name is required')
                  .max(100, 'Menu Name cannot be more than 100 characters')
                  .matches(/^[a-zA-Z0-9\s]+$/, 'Page Group Name should not contain special characters')
                  .nullable(),
                componentTag: Yup.string()
                  .required('Component Tag is required')
                  .max(100, 'Component Tag cannot be more than 100 characters')
                  .matches(/^[a-zA-Z0-9\s]+$/, 'Component Tag should not contain special characters')
                  .nullable(),
                pageGroupAddress: Yup.string()
                  .required('Menu Address is required')
                  .max(100, 'Menu Address cannot be more than 100 characters')
                  .matches(/^[a-zA-Z0-9\s/?=_]+$/, 'Page Group Address is invalid')
                  .nullable(),
                sortOrder: Yup.string()
                  .required('Sort Order is required')
                  .matches(/^[1-9][0-9]*$/, 'Sort order should only contain numbers and cannot be zero')
                  .test('max-sort-order', `Sort Order should not exceed ${newSortOrder}`, function (value) {
                    return parseInt(value) <= newSortOrder;
                  })
                  .nullable(),
                iconMasterKey: Yup.string()
                  .nullable(),
                allow: Yup.string()
                  .max(50, 'Allow cannot be more than 50 characters')
                  .matches(/^[a-zA-Z0-9\s]+$/, 'Allow should not contain special characters')
                  .nullable(),
                isSystemGroup: Yup.boolean().nullable(),
                isFooterGroup: Yup.boolean().nullable(),
                isEnabled: Yup.boolean().nullable(),
                isVisible: Yup.boolean().nullable(),
              })}>
              <Grid spacing={2} container>
                <Grid item xs={12} sm={6}>
                  <InputField label="Menu Name *" name="pageGroupName" disabled={isDisabled} />
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                  <InputField label="Component Tag *" name="componentTag" disabled onChange={() => {}} />
                </Grid> */}
                <Grid item xs={12} sm={6}>
                  <InputField label="Menu Address *" name="pageGroupAddress" disabled onChange={() => {}} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField label="Sort Order *" name="sortOrder" disabled={isDisabled} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SelectField label="Icon" name="iconMasterKey" data={iconData} dataTextField="IconName" dataValueField="IconMasterKey" disable={isDisabled} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField label="Allow" name="allow" disabled={isDisabled} />
                </Grid>
                {/* <Grid item xs={12} sm={3}>
                  <SwitchField label="Is System Group" name="isSystemGroup" labelPlacement='top' disabled={isDisabled} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <SwitchField label="Is Footer Group" name="isFooterGroup" labelPlacement='top' disabled={isDisabled} />
                </Grid> */}
                <Grid item xs={12} sm={1}>
                  <SwitchField label="Enabled" name="isEnabled" labelPlacement='top' disabled={isDisabled} />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <SwitchField label="Visible" name="isVisible" labelPlacement='top' disabled={isDisabled} />
                </Grid>
              </Grid>
            </FormikStep>
          </FormikForm>
        </Box>
      </Grid>
    </Grid>
  );
}
export default MenusCreateEdit;
